import { motion } from 'framer-motion';
import { forwardRef, memo, Ref, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAverageRating } from '~/actions';
import { useRate } from '~/components/Request/multiday/hooks/useRate';
import { useAppSelector } from '~/hooks';
import RateIcon from '~/images/rate-icon.svg';
import { IAverageRating } from '~/reducers/jobReducer/interfaces';
import { Colors } from '~/themes/colors';
import Button from '../Button';
import { Column } from '../Column';
import { InputSelect } from '../InputSelect';
import { Row } from '../Row';
import { Text } from '../Text';
import { Field, Value } from './Field';
import { IInputJobProps } from './Types';

/**
 * Props for the HourlyRate component
 * @extends IInputJobProps<string>
 */
interface IHourlyRateProps extends IInputJobProps<string> {
  /** The professional type (e.g., "dentist", "hygienist") used to fetch average rating data */
  professional: string;
}

const Component = (
  {
    defaultValue,
    isDisabled = false,
    isOpen = false,
    professional,
    value: controlledValue,
    onChange,
    onToggle,
  }: IHourlyRateProps,
  ref: Ref<HTMLDivElement>,
) => {
  const dispatch = useDispatch();

  const average = useAppSelector(
    (state) =>
      ((state.job.averageRating as IAverageRating) || {})?.[professional] || {
        average: 49,
        max: 100,
        min: 18,
      },
  );

  const fetchAverageRatingRef = useRef(true);

  const isControlled = controlledValue !== undefined;

  const {
    disabledBtn,
    industrialAverageColor,
    industrialAverageIcon,
    rates,
    selectedRate,
    getWarningMessage,
    setOfferRate,
  } = useRate({
    averagePrice: average,
    defaultValue: controlledValue || defaultValue,
  });

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: HourlyRate component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  useEffect(() => {
    if (!fetchAverageRatingRef.current) return;

    fetchAverageRatingRef.current = false;
    dispatch(fetchAverageRating());
  }, [dispatch]);

  const options = useMemo(
    () =>
      rates.map((item) => ({
        title: `${item}.00`,
        value: `${item}.00`,
      })),
    [rates],
  );

  const handleValueChange = useCallback(() => {
    onToggle?.();
    onChange?.(selectedRate);
  }, [onChange, onToggle, selectedRate]);

  const ValueComponent = useCallback(() => {
    const currentValue = controlledValue || defaultValue;
    const color = isOpen || !currentValue ? Colors.neutral_300 : Colors.neutral_600;

    return (
      <Value
        text={!isOpen && currentValue ? `$${currentValue}` : '$--.--'}
        style={{
          color,
        }}
      />
    );
  }, [controlledValue, defaultValue, isOpen]);

  return (
    <Field
      ComponentProps={{
        Button: {
          'data-cy': `input-job.hourly-rate.trigger`,
          'data-testid': `input-job.hourly-rate.trigger`,
        },
        Container: { 'data-cy': `input-job.hourly-rate`, 'data-testid': `input-job.hourly-rate` },
      }}
      ValueComponent={ValueComponent}
      IconSrc={RateIcon}
      isDisabled={isDisabled}
      isOpen={isOpen}
      title="Hourly Rate:"
      ref={ref}
      onToggle={onToggle}
    >
      <Column alignItems="center" marginInline={60}>
        <Row alignItems="center" marginBottom={20} gap={12} width={200}>
          <Text bold fontSize={24}>
            $
          </Text>
          <InputSelect
            dataTestId={`input-job.hourly-rate.input`}
            dataCy={`input-job.hourly-rate.input`}
            value={selectedRate}
            setValue={setOfferRate}
            options={options}
          />
        </Row>
        <Row alignItems="center" justifyContent="center">
          <motion.img
            key={industrialAverageColor}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            src={industrialAverageIcon[industrialAverageColor]}
            style={{ marginRight: 6 }}
            alt="green_circle_check"
          />

          {getWarningMessage()}
        </Row>
        <Text
          color={Colors.neutral_500}
          fontSize={15}
          textAlign="center"
          marginBottom={28}
          marginTop={12}
        >
          Our average hourly rate is based on statewide data, and may not precisely reflect rates in
          your local area.
        </Text>
        <Button
          style={{
            width: 154,
            backgroundColor:
              disabledBtn || isDisabled ? Colors.secondary_200 : Colors.secondary_500,
            marginBottom: 0,
          }}
          onClick={handleValueChange}
          disabled={disabledBtn || isDisabled}
          text="Confirm"
          data-testid="confirm-hourly-rate"
          dataCy="confirm-hourly-rate"
        />
      </Column>
    </Field>
  );
};

/**
 * HourlyRate component for selecting and confirming hourly rates.
 *
 * **IMPORTANT: This is an UNCONTROLLED component.**
 * It manages its own internal state via the `useRate` hook and cannot be controlled
 * via props due to legacy architecture constraints.
 *
 * Features:
 * - Uncontrolled rate selection (manages own state)
 * - Fetches average rates from Redux based on professional type
 * - Shows industry average comparison with visual indicators
 * - Requires explicit confirmation via "Confirm" button
 * - Animated feedback based on rate selection vs industry average
 *
 * @example
 * ```tsx
 * <HourlyRate
 *   professional="dentist"
 *   defaultValue="50.00"
 *   onConfirm={(rate) => {
 *     console.log('User confirmed rate:', rate);
 *     // Save to your state/backend
 *   }}
 *   isOpen={isOpen}
 *   onToggle={() => setIsOpen(!isOpen)}
 * />
 * ```
 */
export const HourlyRate = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IHourlyRateProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
